import type { RaRecord } from "react-admin";

import {
  ITS_DATA_RESOURCE,
  buildItsAutoLookupParams,
  isCompleteItsId,
  normalizeItsQuery,
} from "./its-lookup-helpers";

export type ItsAutoLookupGetList = (
  resource: string,
  params: ReturnType<typeof buildItsAutoLookupParams>
) => Promise<{ data: RaRecord[] }>;

export type RunItsAutoLookupSearchArgs = {
  q: unknown;
  getWatchedValue: () => string;
  getLastQuery: () => string;
  setLastQuery: (query: string) => void;
  getList: ItsAutoLookupGetList;
  filter?: Record<string, unknown>;
  onFound: (record: RaRecord, its: string) => void;
  onNotFound?: (its: string) => void;
  onError?: (error: unknown, its: string) => void;
};

export type RunItsAutoLookupSearchResult = "skipped" | "stale" | "ok";

/**
 * Shared auto-lookup fetch: min-length gate, last-query dedupe, stale-response ignore.
 * Used by `useItsAutoLookup` after debounce.
 */
export async function runItsAutoLookupSearch(
  args: RunItsAutoLookupSearchArgs
): Promise<RunItsAutoLookupSearchResult> {
  const t = normalizeItsQuery(args.q);
  if (!isCompleteItsId(t)) return "skipped";
  if (args.getLastQuery() === t) return "skipped";

  try {
    const { data } = await args.getList(
      ITS_DATA_RESOURCE,
      buildItsAutoLookupParams(t, args.filter)
    );
    if (args.getWatchedValue() !== t) return "stale";
    args.setLastQuery(t);
    if (data.length > 0) {
      args.onFound(data[0], t);
    } else {
      args.onNotFound?.(t);
    }
    return "ok";
  } catch (e: unknown) {
    if (args.getWatchedValue() !== t) return "stale";
    args.onError?.(e, t);
    return "ok";
  }
}

/** Effect branch for the auto-lookup hook (incomplete vs schedule search). */
export function planItsAutoLookupEffect(
  normalized: string
): { kind: "incomplete"; value: string } | { kind: "schedule"; value: string } {
  if (!isCompleteItsId(normalized)) {
    return { kind: "incomplete", value: normalized };
  }
  return { kind: "schedule", value: normalized };
}
